import { useEffect } from "react";

const SCROLL_LOCK_CLASS = "ouro-scroll-lock";
const SCROLL_KEYS = new Set(["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "]);

let lockCount = 0;
let savedPaddingRight = "";
let lastTouchX = 0;
let lastTouchY = 0;

function isElementScrollable(el, deltaX, deltaY) {
    if (!(el instanceof HTMLElement)) return false;

    const style = window.getComputedStyle(el);
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;
    const canScrollY =
        (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
        el.scrollHeight > el.clientHeight + 1;
    const canScrollX =
        (overflowX === "auto" || overflowX === "scroll" || overflowX === "overlay") &&
        el.scrollWidth > el.clientWidth + 1;

    if (Math.abs(deltaY) >= Math.abs(deltaX)) {
        if (!canScrollY) return false;
        if (deltaY < 0) return el.scrollTop > 0;
        return el.scrollTop < el.scrollHeight - el.clientHeight - 1;
    }

    if (!canScrollX) return false;
    if (deltaX < 0) return el.scrollLeft > 0;
    return el.scrollLeft < el.scrollWidth - el.clientWidth - 1;
}

function eventCanScrollTarget(target, deltaX, deltaY) {
    let node = target instanceof Element ? target : target?.parentElement;
    while (node && node !== document.documentElement) {
        if (isElementScrollable(node, deltaX, deltaY)) return true;
        node = node.parentElement;
    }
    return false;
}

function isEditableTarget(target) {
    if (!(target instanceof Element)) return false;
    const tag = target.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
    return Boolean(target.closest?.("[contenteditable='true']"));
}

function onWheel(event) {
    if (eventCanScrollTarget(event.target, event.deltaX, event.deltaY)) return;
    event.preventDefault();
}

function onTouchStart(event) {
    const touch = event.touches[0];
    if (!touch) return;
    lastTouchX = touch.clientX;
    lastTouchY = touch.clientY;
}

function onTouchMove(event) {
    const touch = event.touches[0];
    if (!touch) return;
    const deltaX = lastTouchX - touch.clientX;
    const deltaY = lastTouchY - touch.clientY;
    lastTouchX = touch.clientX;
    lastTouchY = touch.clientY;
    if (eventCanScrollTarget(event.target, deltaX, deltaY)) return;
    event.preventDefault();
}

function onKeyDown(event) {
    if (!SCROLL_KEYS.has(event.key) || isEditableTarget(event.target)) return;
    const deltaY =
        event.key === "ArrowUp" || event.key === "PageUp" || event.key === "Home"
            ? -1
            : 1;
    if (eventCanScrollTarget(event.target, 0, deltaY)) return;
    event.preventDefault();
}

function lockBodyScroll() {
    if (typeof document === "undefined") return;

    if (lockCount === 0) {
        const { body, documentElement } = document;
        const scrollbarWidth = window.innerWidth - documentElement.clientWidth;
        savedPaddingRight = body.style.paddingRight;
        documentElement.classList.add(SCROLL_LOCK_CLASS);
        if (scrollbarWidth > 0) {
            body.style.paddingRight = `${scrollbarWidth}px`;
        }
        document.addEventListener("wheel", onWheel, { passive: false });
        document.addEventListener("touchstart", onTouchStart, { passive: true });
        document.addEventListener("touchmove", onTouchMove, { passive: false });
        document.addEventListener("keydown", onKeyDown);
    }

    lockCount += 1;
}

function unlockBodyScroll() {
    if (typeof document === "undefined") return;

    lockCount = Math.max(0, lockCount - 1);
    if (lockCount > 0) return;

    document.removeEventListener("wheel", onWheel);
    document.removeEventListener("touchstart", onTouchStart);
    document.removeEventListener("touchmove", onTouchMove);
    document.removeEventListener("keydown", onKeyDown);
    document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
    document.body.style.paddingRight = savedPaddingRight;
    savedPaddingRight = "";
}

export function useLockBodyScroll(locked) {
    useEffect(() => {
        if (!locked) return undefined;
        lockBodyScroll();
        return unlockBodyScroll;
    }, [locked]);
}
